import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useAuthStore } from './store';
import { completeLogin } from './oidc';

/**
 * /auth/callback — exchanges the authorization code (PKCE) for tokens, stores
 * them in the solid-auth store, and returns the user to where they started.
 */
export function CallbackPage() {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invocation — the auth code is
    // single-use, so the exchange must run exactly once.
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const idpError = params.get('error');
    if (idpError) {
      setError(params.get('error_description') ?? idpError);
      return;
    }
    const code = params.get('code');
    if (!code) {
      setError('Missing authorization code in the callback URL.');
      return;
    }

    completeLogin(code, params.get('state') ?? '')
      .then(({ accessToken, refreshToken, user, returnTo }) => {
        useAuthStore.getState().setAuth({ accessToken, refreshToken, user });
        navigate(returnTo, { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Sign-in failed.');
      });
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50 px-4">
      {error ? (
        <div className="card w-full max-w-md p-8 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-warning" aria-hidden />
          <h1 className="mt-4 text-heading-3 text-secondary">Sign-in didn&rsquo;t finish</h1>
          <p className="mt-2 text-body-sm text-grey-500">{error}</p>
          <Link to="/login" className="btn-primary mt-6 inline-flex">
            Back to sign in
          </Link>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-grey-500">
          <Loader2 className="h-8 w-8 animate-spin text-secondary" aria-hidden />
          <p className="text-body-sm">Completing sign-in&hellip;</p>
        </div>
      )}
    </div>
  );
}
